import React, { useRef, useState } from "react";
import { DesignRef, OrderUpdate, shortDate, stageLabel, uploadDesignRefs } from "../../services/partnerNetworkApi";

const HEALTH: Record<string, { label: string; tone: string }> = {
  ON_TRACK: { label: "On track", tone: "ok" },
  DUE_SOON: { label: "Due soon", tone: "soon" },
  OVERDUE: { label: "Update overdue", tone: "late" },
  STUCK: { label: "Stuck", tone: "stuck" },
};

export const HealthDot = ({ health, withLabel }: { health?: string | null; withLabel?: boolean }) => {
  const h = HEALTH[health || ""] || HEALTH.ON_TRACK;
  return (
    <span className={`pn-health is-${h.tone}`} title={h.label}>
      <i aria-hidden="true" />
      {withLabel ? h.label : <span className="pn-sr">{h.label}</span>}
    </span>
  );
};

export const MODE_LABEL: Record<string, string> = {
  HKP_EXISTING: "On HKP",
  SPONSORED: "Sponsored",
  LINK_ONLY: "Link only",
};

export const STATUS_LABEL: Record<string, string> = {
  SENT: "Sent",
  COUNTERED: "Counter-offer",
  ACCEPTED: "Agreed",
  DECLINED: "Declined",
  CANCELLED: "Cancelled",
  INVITED: "Invited",
  ACTIVE: "Active",
  PAUSED: "Paused",
  ENDED: "Ended",
};

const KIND_LABEL: Record<string, string> = {
  STAGE: "Stage",
  PHOTO: "Photos",
  PROGRESS: "Progress",
  NOTE: "Note",
  DELAY: "Problem / delay",
  DOCUMENT: "Document",
};

export const updateText = (u: OrderUpdate): string => {
  if (u.kind === "STAGE") return stageLabel(u.payload.stage);
  if (u.kind === "PROGRESS") return `${u.payload.piecesDone} pieces done`;
  return u.payload.text || KIND_LABEL[u.kind] || u.kind;
};

/**
 * The order timeline. With `onToggle`, the viewer's own items get a "Share with {designer}" switch;
 * NEVER-policy items show locked.
 */
export const Timeline = ({
  updates,
  shareWith,
  onToggle,
  busy,
}: {
  updates: OrderUpdate[];
  shareWith?: string;
  onToggle?: (u: OrderUpdate, share: boolean) => void;
  busy?: boolean;
}) => {
  if (!updates.length) {
    return <p className="pn-muted">No updates yet.</p>;
  }
  return (
    <ol className="pn-timeline">
      {[...updates].reverse().map((u) => (
        <li key={u.id} className={u.kind === "DELAY" ? "is-delay" : ""}>
          <div className="pn-timeline__meta">
            <span className="pn-timeline__kind">{KIND_LABEL[u.kind] || u.kind}</span>
            <time>{shortDate(u.at)}</time>
            {u.channel && u.channel !== "APP" ? <span className="pn-chip">{u.channel === "LINK" ? "Link" : "WhatsApp"}</span> : null}
            {u.byName ? <span className="pn-muted">{u.byName}</span> : null}
          </div>
          <div className="pn-timeline__body">{updateText(u)}</div>
          {u.payload.files?.length ? (
            <div className="pn-files">
              {u.payload.files.map((f, i) =>
                f.contentType?.startsWith("image/") && f.url ? (
                  <a key={i} href={f.url} target="_blank" rel="noreferrer"><img src={f.url} alt={f.name || "Photo"} /></a>
                ) : (
                  <a key={i} className="pn-file" href={f.url} target="_blank" rel="noreferrer">{f.name || "File"}</a>
                )
              )}
            </div>
          ) : null}
          {onToggle && u.mine ? (
            <label className={`pn-share${u.locked ? " is-locked" : ""}`}>
              <input
                type="checkbox"
                role="switch"
                checked={u.visibility === "SHARED"}
                disabled={busy || (u.locked && u.visibility !== "SHARED")}
                onChange={(e) => onToggle(u, e.target.checked)}
              />
              <span className="pn-switch" aria-hidden="true" />
              {u.locked && u.visibility !== "SHARED"
                ? "Internal — your share settings keep this kind private"
                : u.visibility === "SHARED"
                ? `Shared with ${shareWith}`
                : `Share with ${shareWith}?`}
            </label>
          ) : null}
        </li>
      ))}
    </ol>
  );
};

export type ShownRef = { key?: string; name?: string; url?: string; preview?: string; contentType?: string };

/** Design references: thumbnails (images) or file chips (PDF). With onChange, the designer can add and remove. */
export const DesignFiles = ({
  refs,
  onChange,
  disabled,
}: {
  refs: ShownRef[];
  onChange?: (refs: ShownRef[]) => void;
  disabled?: boolean;
}) => {
  const input = useRef<HTMLInputElement | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const add = async (files: FileList | null) => {
    if (!files?.length || !onChange) return;
    setBusy(true);
    setError("");
    const res = await uploadDesignRefs(files);
    setBusy(false);
    if (res.error) setError(res.error);
    if (res.refs.length) onChange([...refs, ...res.refs.map((r: DesignRef) => r)]);
  };
  if (!refs.length && !onChange) return null;
  return (
    <div className="pn-refs">
      {refs.map((ref, i) => {
        const src = ref.preview || ref.url;
        const isImage = (ref.contentType || "").startsWith("image/") || (!ref.contentType && Boolean(src));
        return (
          <div className="pn-ref" key={ref.key || i}>
            {isImage && src ? (
              <a href={ref.url || src} target="_blank" rel="noreferrer"><img src={src} alt={ref.name || "Design"} /></a>
            ) : (
              <a className="pn-ref__file" href={ref.url} target="_blank" rel="noreferrer">{ref.name || "File"}</a>
            )}
            {onChange && !disabled ? (
              <button type="button" className="pn-ref__remove" aria-label={`Remove ${ref.name || "file"}`}
                onClick={() => onChange(refs.filter((_, j) => j !== i))}>×</button>
            ) : null}
          </div>
        );
      })}
      {onChange && !disabled ? (
        <>
          <button type="button" className="pn-ref pn-ref__add" disabled={busy || refs.length >= 12} onClick={() => input.current?.click()}>
            {busy ? "Uploading…" : "+ Design file"}
          </button>
          <input ref={input} type="file" accept="image/*,application/pdf" multiple hidden
            onChange={(e) => { add(e.target.files); e.target.value = ""; }} />
        </>
      ) : null}
      {error ? <p className="pn-limit" style={{ flexBasis: "100%" }}>{error}</p> : null}
    </div>
  );
};

/** Designer-only labels such as "Bengal jamdani"; used to filter the board. */
export const GroupsEditor = ({ value, onSave, onCancel }: {
  value: string[]; onSave: (groups: string[]) => void; onCancel: () => void;
}) => {
  const [groups, setGroups] = useState<string[]>(value);
  const [draft, setDraft] = useState("");
  const add = () => {
    const g = draft.trim();
    if (g && !groups.includes(g)) setGroups([...groups, g]);
    setDraft("");
  };
  return (
    <div className="pn-groups">
      <div className="pn-list__meta">
        {groups.map((g) => (
          <span key={g} className="pn-chip">
            {g}
            <button type="button" aria-label={`Remove ${g}`} onClick={() => setGroups(groups.filter((x) => x !== g))}>×</button>
          </span>
        ))}
      </div>
      <div className="pn-form" style={{ marginTop: 8 }}>
        <label className="is-wide">
          Add a group
          <input value={draft} placeholder="e.g. Bengal jamdani" onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }} />
        </label>
      </div>
      <div className="pn-actions">
        <button type="button" className="kh-btn" onClick={add} disabled={!draft.trim()}>Add</button>
        <button type="button" className="kh-btn" onClick={onCancel}>Cancel</button>
        <button type="button" className="kh-btn is-primary" onClick={() => onSave(draft.trim() && !groups.includes(draft.trim()) ? [...groups, draft.trim()] : groups)}>Save groups</button>
      </div>
    </div>
  );
};
